import AccountCircle from '@mui/icons-material/AccountCircle';
import MenuIcon from '@mui/icons-material/Menu';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import { Badge, Box, Button, Chip, Divider, Tooltip } from "@mui/material";
import AppBar from '@mui/material/AppBar';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Switch from '@mui/material/Switch';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import { useState } from "react";
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAutenticacaoContext } from "../../contexts/AutenticacaoContext";
import { Environment } from "../../config/environment";
import { useMenuLateralContext } from "../../contexts/MenuLateralContext";
import { useNotificacoesContext } from "../../contexts/NotificacoesContext";
import { useHeaderContext } from '../../contexts/HeaderContext';
import { useThemeContext } from "../../contexts/ThemeContext";
import { useTamanhoTela } from '../../hooks/useTamanhoTela';
import { Storefront } from '@mui/icons-material';
import { useLetras } from '../../hooks/useLetras';

interface IHeaderProps {
  tituloModulo: string;
  /** Exibe o botão que recolhe/expande o menu lateral. */
  exibirMenu?: boolean;
}

export const Header = ({ tituloModulo, exibirMenu = false }: IHeaderProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { usuario, logout, estabelecimento, organizacoes } = useAutenticacaoContext();
  const { logo, customTheme, themeName, toggleTheme } = useThemeContext();
  const { recolhido, alternarRecolhido, abrirMenuMobile } = useMenuLateralContext();
  const { modeloTela } = useTamanhoTela();
  const { tools } = useHeaderContext();
  const { totalNotificacoes } = useNotificacoesContext();

  const mobile = modeloTela === "mobile";

  const alternarMenu = () => (mobile ? abrirMenuMobile() : alternarRecolhido());

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const { titulo } = useLetras();

  return (
    <AppBar
      position="fixed"
      sx={{
        zIndex: 9999,
        height: "50px",
        display: "flex",
        alignItems: "space-between",
        justifyContent: "center",
        background: theme => theme.palette.background.paper,
        boxShadow: 'none',
        borderBottom: theme => `1px solid ${theme.palette.divider}` // Substitui o boxShadow pelo border-bottom
      }}
    >
      <Toolbar>
        <Link to={estabelecimento ? "/dashboard" : '/estabelecimentos'} style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
          <img
            src={logo}
            style={{ width: "55px", height: "40px", objectFit: "contain", marginRight: "1rem" }}
            alt="Space Food"
          />
        </Link>
        <Box sx={{ mr: "1rem", display: "flex", alignItems: "center", justifyContent: "center", height: "50px" }}>
          <Divider orientation="vertical" variant="middle" flexItem />
        </Box>
        {exibirMenu && (
          <Tooltip title={mobile ? "Abrir menu" : recolhido ? "Expandir menu" : "Recolher menu"}>
            <IconButton
              size="large"
              edge="start"
              aria-label="menu"
              onClick={alternarMenu}
            >
              {/* No mobile o menu fica escondido — o badge avisa que há pendência lá dentro. */}
              <Badge
                color="error"
                overlap="circular"
                badgeContent={totalNotificacoes > 99 ? "99+" : totalNotificacoes}
                invisible={!mobile || totalNotificacoes === 0}
              >
                {mobile || recolhido ? <MenuIcon /> : <MenuOpenIcon />}
              </Badge>
            </IconButton>
          </Tooltip>
        )}
        <Box sx={{ flexGrow: 1, display: "flex", alignItems: "center" }}>
          <Typography variant="subtitle2" component="div" sx={{ color: 'text.primary' }}>
            {tituloModulo}
          </Typography>
          {tools}
        </Box>
        {!mobile && (
          <Typography variant='subtitle2' sx={{ color: 'text.primary' }}>
            Olá <b>{usuario?.nome}</b>
          </Typography>
        )}
        <div>
          <IconButton
            size="large"
            aria-label="account of current user"
            aria-controls="menu-appbar"
            aria-haspopup="true"
            onClick={handleMenu}
          >
            <AccountCircle />
          </IconButton>
          <Menu
            id="menu-appbar"
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            sx={{ zIndex: 9999, minWidth: "500px" }}
          >
            {estabelecimento && (
              <>
                <MenuItem style={{ pointerEvents: 'none' }}>
                  <Chip label={
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Storefront sx={{ mr: 1 }} />
                      <Typography fontWeight={"bold"} fontSize={12}>{estabelecimento.nomeFantasia}</Typography>
                    </Box>
                  } />
                </MenuItem>
                <Divider orientation='horizontal' />
              </>
            )}
            <Link to="/estabelecimentos"><MenuItem>Meus estabelecimentos</MenuItem></Link>
            {!!organizacoes?.length && (
              <Link to="/organizacoes"><MenuItem>Minha organização</MenuItem></Link>
            )}
            {estabelecimento?.uuid === Environment.UUID_SPACEFOOD && (
              <Link to="/admin/organizacoes"><MenuItem>Organizações (admin)</MenuItem></Link>
            )}
            <Link to="/usuarios/meu-perfil"><MenuItem>Meu perfil</MenuItem></Link>
            {customTheme && (
              <>
                <Divider orientation='horizontal' />
                <MenuItem>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                    <Typography>Tema escuro</Typography>
                    <Switch
                      checked={themeName === 'dark'}
                      onChange={toggleTheme}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </Box>
                </MenuItem>
              </>
            )}
            <MenuItem onClick={logout}>Sair</MenuItem>
          </Menu>
        </div>
      </Toolbar>
    </AppBar>

  );
}